import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Col,
  ListGroup,
  Loading,
  Modal,
  Overlay,
  Row,
  Text,
  TextArea,
} from '@cubeyou-team/hx-react-components';

import { QueryBuilderConstants } from 'src/modules/query-builder/constants';
import { createOnDropObj } from 'src/utils';
import { findGroupLevel, isEmpty } from 'src/utils/misc';
import { isGroupOnlyEmptyRow } from 'src/operations/query';
import translations from 'src/lang';

import {
  QueryItem,
  QueryActions,
  ConversationQueryActions,
  QueryOperator,
} from '../';
import {
  StyledQueryGroup,
  StyledQueryGroupEntityList,
  StyledQueryGroupEntityWires,
  StyledQueryEntityWire,
  StyledIsDraggingPointerEvents,
  StyledEmptyGroupText,
} from './StyledQueryGroup';

const { QUERY_BUILDER } = translations['en-US'];

const availableOperators = [
  {
    id: 'OR',
    label: 'OR (Any)',
  },
  {
    id: 'AND',
    label: 'AND (All)',
  },
];
const QueryGroup = ({
  group,
  index,
  isOuter,
  parentGroupId,
  entitiesInfos,
  conversationQueries,
  entityDragging,
  onAddEntityRequest,
  onUpdateEntityRequest,
  onChangeGroupOperatorRequest,
  onEntityDragToggle,
  onDeleteEntityRequest,
  onItemDrop,
  onItemDragStart,
  onRequireMissingItemRequest,
  queryBuilderType,
  color,
  onSelectGroupRequest,
  onChangeGroupAtLeastNumberRequest,
  onAddEntitiesListRequest,
}) => {
  const { arguments_list } = group;
  const moreArguments = arguments_list.length > 1;
  const morethenOneArguments = arguments_list.length > 0;
  const onlyEmptyRow = isGroupOnlyEmptyRow(
    group,
    entitiesInfos,
    conversationQueries
  );
  const [
    showUploadConversationItemsModal,
    setShowUploadConversationItemsModal,
  ] = useState(false);
  const [conversationItemsText, setConversationItemsText] = useState('');
  const [dragEntered, SetDragEntered] = useState(false);
  const [highlighted, setHighlighted] = useState();
  const selectedTarget = useSelector(
    (state) => state.queryBuilder.target_selected
  );
  const query = useSelector(
    (state) => state.queryBuilder.targets[selectedTarget]
  );
  const groupLoaded = query && query.groupLoaded;
  const level = query && findGroupLevel(query.target_query, group.id, 0);
  const wiresColor =
    query &&
    level !== -1 &&
    QueryBuilderConstants.groupOperatorColors[level % 7][0];

  const isFirstGroup = group.id === 0 || group.id === '0';

  const handleCloseUploadConversationItemsModal = useCallback(() => {
    setConversationItemsText('');
    setShowUploadConversationItemsModal(false);
  }, []);

  const handleAddConversationItems = useCallback(
    (e) => {
      e.preventDefault();
      handleCloseUploadConversationItemsModal();
      const items = conversationItemsText
        .split('\n')
        .filter((item) => !isEmpty(item));

      if (!isEmpty(items)) {
        onAddEntitiesListRequest(group.id, items);
      }
      e.stopPropagation();
    },
    [
      group.id,
      onAddEntitiesListRequest,
      handleCloseUploadConversationItemsModal,
      conversationItemsText,
    ]
  );

  return (
    <StyledQueryGroup
      isOuter={isOuter}
      dragEntered={dragEntered}
      data-group-id={group.id}
      data-parent-id={parentGroupId}
      groupColor={color}
      onClick={(e) => {
        e.preventDefault();
        if (group.id !== 0) {
          onSelectGroupRequest(group.id);
        }
        e.stopPropagation();
      }}
      isFirstGroupsChild={parentGroupId === 0}
    >
      <Loading isLoading={group.id === groupLoaded}>
        <StyledIsDraggingPointerEvents
          onDragEnter={(e) => {
            SetDragEntered(true);
            e.stopPropagation();
            e.preventDefault();
          }}
          onDragLeave={(e) => {
            SetDragEntered(false);
          }}
          onDrop={(e) => {
            SetDragEntered(false);

            onItemDrop(createOnDropObj(e, group.id));
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          entityDragging={entityDragging}
          isOuter={isOuter}
        />
        {queryBuilderType !== 'conversation' && group.id !== 0 && (
          <QueryActions
            group={group}
            numberOfArguments={arguments_list.length}
            canDelete={!isOuter && !onlyEmptyRow}
            parentGroupId={parentGroupId}
            index={index}
            showOperator={
              (group.id !== 0 && morethenOneArguments) || moreArguments
            }
            onChangeGroupOperatorRequest={onChangeGroupOperatorRequest}
            onAddEntityRequest={onAddEntityRequest}
            onDeleteGroupRequest={onDeleteEntityRequest}
            onHighlightGroup={(value) => setHighlighted(value)}
            onChangeGroupAtLeastNumberRequest={
              onChangeGroupAtLeastNumberRequest
            }
          />
        )}
        {queryBuilderType === 'conversation' && group.id !== 0 && (
          <ConversationQueryActions
            group={group}
            canDelete={!isOuter && !onlyEmptyRow}
            parentGroupId={parentGroupId}
            index={index}
            showOperator={moreArguments}
            onHighlightGroup={(value) => setHighlighted(value)}
          />
        )}
        <StyledQueryGroupEntityList
          hasWires={(group.id !== 0 && morethenOneArguments) || moreArguments}
        >
          {group.id !== 0 &&
            ((morethenOneArguments && queryBuilderType !== 'conversation') ||
              moreArguments) && (
              <StyledQueryGroupEntityWires
                wiresColor={highlighted && wiresColor}
              />
            )}
          {isEmpty(group.arguments_list) && !isFirstGroup && (
            <StyledEmptyGroupText>
              <Text size="small">{QUERY_BUILDER.emptyGroup}</Text>
            </StyledEmptyGroupText>
          )}
          {arguments_list.map((query_item, index) => {
            return (
              <React.Fragment key={`entity-${index}`}>
                <StyledQueryEntityWire
                  showWire={
                    (group.id !== 0 &&
                      morethenOneArguments &&
                      queryBuilderType !== 'conversation') ||
                    moreArguments
                  }
                  position={group.id === 0 ? 'bottom-top' : 'left'}
                  wireColor={highlighted && wiresColor}
                >
                  <QueryItem
                    item={query_item}
                    parentGroupId={group.id}
                    entitiesInfos={entitiesInfos}
                    conversationQueries={conversationQueries}
                    index={index}
                    entityDragging={entityDragging}
                    onEntityDragToggle={onEntityDragToggle}
                    onAddEntityRequest={onAddEntityRequest}
                    onUpdateEntityRequest={onUpdateEntityRequest}
                    onDeleteEntityRequest={onDeleteEntityRequest}
                    canDeleteRow={!onlyEmptyRow}
                    onChangeGroupOperatorRequest={onChangeGroupOperatorRequest}
                    onItemDragStart={onItemDragStart}
                    onItemDrop={onItemDrop}
                    onRequireMissingItemRequest={onRequireMissingItemRequest}
                    queryBuilderType={queryBuilderType}
                    color={(highlighted || query_item.selected) && wiresColor}
                    onSelectGroupRequest={onSelectGroupRequest}
                    onChangeGroupAtLeastNumberRequest={
                      onChangeGroupAtLeastNumberRequest
                    }
                    onAddEntitiesListRequest={onAddEntitiesListRequest}
                  />
                </StyledQueryEntityWire>
                {group.id === 0 && index < arguments_list.length - 1 && (
                  <QueryOperator
                    level={level}
                    operator={group.operator}
                    onOperatorChange={(operator) => {
                      onChangeGroupOperatorRequest(group.id, operator);
                    }}
                    onHighlightOperator={(value) => setHighlighted(value)}
                    availableOperators={availableOperators}
                  />
                )}
              </React.Fragment>
            );
          })}
        </StyledQueryGroupEntityList>
        <Box pT="small" pB="small">
          <Row halign="right">
            {group.id !== 0 && (
              <Col span="auto">
                <Button
                  disabled={false}
                  className="button-smaller"
                  icon="add"
                  onClick={(e) => {
                    e.preventDefault();
                    onAddEntityRequest(group.id, 'item');
                    e.stopPropagation();
                  }}
                  size="small"
                  type="button"
                  variant="outlined"
                  id="gtAddRow"
                >
                  row
                </Button>
              </Col>
            )}
            {queryBuilderType === 'conversation' && group.id !== 0 && (
              <Col span="auto">
                <Button
                  className="button-smaller"
                  icon="add"
                  id="gtAddList"
                  onClick={() => setShowUploadConversationItemsModal(true)}
                  size="small"
                  type="button"
                  variant="outlined"
                >
                  list
                </Button>
              </Col>
            )}
            <Col span="auto">
              <Button
                disabled={false}
                className="button-smaller"
                icon="add"
                id="gtAddGroup"
                onClick={(e) => {
                  e.preventDefault();
                  onAddEntityRequest(group.id, 'group');
                  e.stopPropagation();
                }}
                size="small"
                type="button"
                variant="outlined"
              >
                group
              </Button>
            </Col>
          </Row>
        </Box>
      </Loading>
      {showUploadConversationItemsModal && (
        <Overlay isOpen>
          <Modal
            title="Upload List"
            footer={
              <ListGroup
                direction="row"
                items={[
                  <Button
                    size="small"
                    disabled={isEmpty(conversationItemsText)}
                    onClick={(e) => handleAddConversationItems(e)}
                  >
                    Upload List
                  </Button>,
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCloseUploadConversationItemsModal()}
                  >
                    Close
                  </Button>,
                ]}
              />
            }
            handleClose={() => handleCloseUploadConversationItemsModal()}
          >
            <Box>
              <Box pB="small">
                <Text size="small">Each item must be on a single line</Text>
              </Box>
              <TextArea
                data-test-id="items-text"
                style={{ height: '300px' }}
                name="items-text"
                value={conversationItemsText}
                onChange={(e) => setConversationItemsText(e.target.value)}
              />
            </Box>
          </Modal>
        </Overlay>
      )}
    </StyledQueryGroup>
  );
};

QueryGroup.propTypes = {
  block: PropTypes.object,
  isOuter: PropTypes.bool,
  onAddEntityRequest: PropTypes.func,
  onGroupDeleteRequest: PropTypes.func,
  onChangeGroupOperatorRequest: PropTypes.func,
  onEntityDragToggle: PropTypes.func,
  queryBuilderType: PropTypes.string,
};

QueryGroup.defaultProps = {
  operator: 'OR',
  isFirst: false,
};

export default QueryGroup;
